import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, MapPin, IndianRupee, Home } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Analysis = () => {
  const [searchParams] = useSearchParams();
  const location = searchParams.get('location') || 'Mumbai';
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/listings/public`);
        setListings(response.data);
      } catch (error) {
        console.error('Error fetching listings:', error);
      }
    };
    fetchListings();
  }, []);

  const priceData = [
    { month: 'Jan', price: 4500000 },
    { month: 'Feb', price: 4700000 },
    { month: 'Mar', price: 4600000 },
    { month: 'Apr', price: 4900000 },
    { month: 'May', price: 5100000 },
    { month: 'Jun', price: 5300000 },
  ];

  const areaData = [
    { area: 'Andheri', avgPrice: 12500 },
    { area: 'Bandra', avgPrice: 18500 },
    { area: 'Powai', avgPrice: 14200 },
    { area: 'Goregaon', avgPrice: 11800 },
    { area: 'Malad', avgPrice: 10200 },
  ];

  const stats = [
    {
      label: 'Avg Property Price',
      value: '₹52.3L',
      change: '+12.5%',
      isPositive: true,
      icon: IndianRupee,
    },
    {
      label: 'Total Listings',
      value: listings.length.toString(),
      change: `+${Math.floor(listings.length * 0.08)}`,
      isPositive: true,
      icon: Home,
    },
    {
      label: 'Price per Sq.Ft',
      value: '₹14,250',
      change: '+8.3%',
      isPositive: true,
      icon: TrendingUp,
    },
    {
      label: 'Market Trend',
      value: 'Growing',
      change: 'Strong demand',
      isPositive: true,
      icon: TrendingUp,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-2 text-slate-600 mb-4">
            <MapPin className="w-5 h-5" />
            <span className="font-medium">{location}</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="analysis-title">
            Market Analysis
          </h1>
          <p className="text-lg text-slate-600">
            Real-time insights and trends for {location}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md smooth-transition"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-slate-900" />
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  stat.isPositive ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {stat.isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {stat.change}
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
            data-testid="price-trend-chart"
          >
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-6">
              Price Trend (6 Months)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={priceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{
                    background: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#0F172A"
                  strokeWidth={2}
                  dot={{ fill: '#0F172A', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
            data-testid="area-comparison-chart"
          >
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-6">
              Area Comparison (per Sq.Ft)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={areaData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="area" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{
                    background: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="avgPrice" fill="#0F172A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-xl p-8 text-center"
        >
          <h3 className="font-display text-2xl font-semibold text-slate-900 mb-4">
            Want to see actual listings?
          </h3>
          <p className="text-slate-600 mb-6">
            Join as a broker or list your properties as a company
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="bg-slate-900 text-white rounded-lg px-8 py-3 font-medium hover:bg-slate-800 smooth-transition shadow-sm"
            data-testid="cta-button"
          >  Get Started
          </button>
        </motion.div>
      </div>
    </div>
  );
};